import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  Text,
  VStack,
  useColorModeValue,
  Container,
  HStack,
  Icon,
  Badge,
  Flex,
  Divider,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Button,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  useToast
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { FaChartLine, FaRedo, FaUserFriends, FaComments, FaClock as FaClockSolid, FaExchangeAlt } from "react-icons/fa";
import { fetchAuthSession } from '@aws-amplify/auth';

const MotionBox = motion(Box);
const MotionText = motion(Text);

// Lambda function endpoint
const STATISTICS_API_ENDPOINT = "https://gls3kwug34.execute-api.eu-north-1.amazonaws.com/prod/user-statistics";

export default function Usage() {
  const [usageData, setUsageData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const toast = useToast();
  
  const bgColor = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.800", "whiteAlpha.900");
  const accentColor = useColorModeValue("teal.500", "teal.300");
  const cardBg = useColorModeValue("gray.50", "gray.700");
  const statCardBg = useColorModeValue("white", "gray.700");

  // Helper function to calculate average session time
  const calculateAverageSessionTime = (messages) => {
    if (!messages || messages.length === 0) return 0;
    return Math.round(messages.length * 0.10); // Placeholder: 25 seconds per message
  };

const extractUserMessage = (fullInput) => {
  if (!fullInput) {
    return "No input available";
  }
  
  // Return the full message as-is since API now provides clean content
  return fullInput;
};
  // Function to fetch user statistics from Lambda
const fetchUserStatistics = async () => {
  setIsLoading(true);
  setError(null);
  
  try {
    // Get the current user's auth session
    const authSession = await fetchAuthSession();
    const idToken = authSession.tokens?.idToken?.toString();
    
    if (!idToken) {
      throw new Error("No authentication token available");
    }
    
    // Extract user ID and email from the token payload
    const userInfo = authSession.tokens?.idToken?.payload;
    const userId = userInfo?.sub || userInfo?.["cognito:username"];
    const userEmail = userInfo?.email || "No email available";
    
    if (!userId) {
      throw new Error("Could not retrieve user identity");
    }
    
    console.log("Using user ID as API path:", userId);
    
    // Build the URL with the user ID as the apiPath parameter
    const url = `${STATISTICS_API_ENDPOINT}?apiPath=${userId}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json'
      }
    });
      
    if (!response.ok) {
      throw new Error(`API responded with status: ${response.status}`);
    }
      
    // Parse the response
    const responseData = await response.json();

    // Handle nested JSON in the body field
    let parsedData;
    if (responseData.body) {
      try {
        // The body is a JSON string that needs to be parsed
        parsedData = typeof responseData.body === 'string' 
          ? JSON.parse(responseData.body) 
          : responseData.body;
      } catch (error) {
        console.error("Error parsing body JSON:", error);
        throw new Error("Failed to parse API response data");
      }
    } else {
      // If no body property, use the response data directly
      parsedData = responseData;
    }

    // Check if data has the expected structure
    if (!parsedData || !parsedData.success) {
      throw new Error("Invalid API response format");
    }

    // Extract endpoint and stats data
    const endpointData = parsedData.endpoint || {};
    const statsData = parsedData.stats || {};
      
    // Set defaults for any missing values to prevent null/undefined errors
    setUsageData({
      endpointName: userEmail, // Display the user's email instead of API endpoint name
      endpointPath: endpointData.path || "",
      totalSessions: statsData.requests || 0,
      totalConversations: (statsData.recent_messages || []).length || 0,
      totalInputChars: statsData.input_chars || 0,
      totalOutputChars: statsData.output_chars || 0,
      avgSessionTime: calculateAverageSessionTime(statsData.recent_messages),
      conversionRate: ((statsData.output_chars / (statsData.input_chars || 1)) * 100).toFixed(1),
      lastUpdated: statsData.last_used_formatted || new Date().toLocaleString(),
      recentMessages: statsData.recent_messages || []
    });
    
    toast({
      title: "Statistics updated",
      description: `Loaded data for ${userEmail}`,
      status: "success",
      duration: 3000,
      isClosable: true,
    });
      
    } catch (err) {
      console.error("Error fetching statistics:", err);
      setError(err.message || "Failed to fetch usage statistics");
      
      toast({
        title: "Error loading statistics",
        description: err.message || "Could not retrieve your usage data",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchUserStatistics();
  }, []);

  // StatCard component with null checks
  const StatCard = ({ title, value, icon, accentColor }) => (
    <Box
      bg={statCardBg}
      p={6}
      borderRadius="xl"
      boxShadow="md"
      borderLeft="4px solid"
      borderLeftColor={accentColor}
    >
      <Flex justifyContent="space-between" alignItems="center">
        <Box>
          <Stat>
            <StatLabel color="gray.500">{title}</StatLabel>
            <StatNumber fontSize="2xl">{value}</StatNumber>
            <StatHelpText>
              Last updated: {usageData?.lastUpdated || 'Unknown'}
            </StatHelpText>
          </Stat>
        </Box>
        <Box p={2} borderRadius="full" bg={`${accentColor}20`}>
          <Icon as={icon} color={accentColor} boxSize={6} />
        </Box>
      </Flex>
    </Box>
  );

  // Render statistics component
  const renderStatistics = () => {
    if (!usageData) return null;
    
    return (
      <Box mt={8}>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={6}>
          <StatCard 
            title="Total Requests" 
            value={usageData.totalSessions.toLocaleString()} 
            icon={FaUserFriends} 
            accentColor="blue.500"
          />
          
          <StatCard 
            title="Recent Messages" 
            value={usageData.totalConversations.toLocaleString()} 
            icon={FaComments} 
            accentColor="green.500"
          />
          
          <StatCard 
            title="Avg. Response Time" 
            value={`${usageData.avgSessionTime}s`} 
            icon={FaClockSolid} 
            accentColor="purple.500"
          />
          
          <StatCard 
            title="Output/Input Ratio" 
            value={`${usageData.conversionRate}%`}
            icon={FaExchangeAlt} 
            accentColor="orange.500"
          />
        </SimpleGrid>
        
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          <Box 
            bg={cardBg} 
            p={5} 
            borderRadius="lg" 
            boxShadow="sm"
            borderLeft="4px solid"
            borderLeftColor="cyan.400"
          >
            <Heading size="sm" mb={4}>API Input/Output Volume</Heading>
            <Flex justify="space-between" align="center">
              <Box>
                <Text fontSize="sm" color="gray.500">Total Input</Text>
                <Text fontSize="2xl" fontWeight="bold">
                  {(usageData.totalInputChars / 1024).toFixed(1)} KB
                </Text>
                <Text fontSize="sm" color="gray.600">
                  {usageData.totalInputChars.toLocaleString()} characters
                </Text>
              </Box>
              <Box bg="blue.100" h="50px" w="2px" mx={4} />
              <Box>
                <Text fontSize="sm" color="gray.500">Total Output</Text>
                <Text fontSize="2xl" fontWeight="bold">
                  {(usageData.totalOutputChars / 1024).toFixed(1)} KB
                </Text>
                <Text fontSize="sm" color="gray.600">
                  {usageData.totalOutputChars.toLocaleString()} characters
                </Text>
              </Box>
            </Flex>
          </Box>
          
          <Box 
            bg={cardBg} 
            p={5} 
            borderRadius="lg" 
            boxShadow="sm"
            borderLeft="4px solid"
            borderLeftColor="purple.400"
          >
            <Heading size="sm" mb={4}>API Details</Heading>
            <VStack align="start" spacing={2}>
              <Flex justify="space-between" width="100%">
                <Text color="gray.500">Endpoint Name:</Text>
                <Text fontWeight="medium">{usageData.endpointName}</Text>
              </Flex>
              <Flex justify="space-between" width="100%">
                <Text color="gray.500">API Path:</Text>
                <Text fontWeight="medium" fontFamily="mono">
                  {usageData.endpointPath}
                </Text>
              </Flex>
              <Flex justify="space-between" width="100%">
                <Text color="gray.500">Last Updated:</Text>
                <Text fontWeight="medium">{usageData.lastUpdated}</Text>
              </Flex>
            </VStack>
          </Box>
        </SimpleGrid>
      </Box>
    );
  };
    
  // Render recent messages component
  const renderRecentMessages = () => {
    if (!usageData?.recentMessages?.length) {
      return (
        <Box p={6} textAlign="center" bg={cardBg} borderRadius="md">
          <Text color="gray.500">No recent conversations available</Text>
        </Box>
      );
    }
    
    return (
      <VStack align="stretch" spacing={4} mt={8}>
        <Flex justify="space-between" align="center">
          <Heading size="md">Recent Interactions</Heading>
          <Badge colorScheme="purple" fontSize="0.9em" py={1} px={3} borderRadius="md">
            API: {usageData.endpointName || "Unknown"}
          </Badge>
        </Flex>
        
        {usageData.recentMessages.map((message, idx) => (
          <Box 
            key={idx}
            bg={cardBg}
            p={5}
            borderRadius="lg"
            boxShadow="sm"
            borderLeft="4px solid"
            borderLeftColor={idx % 2 === 0 ? "blue.400" : "teal.400"}
          >
            <Flex justify="space-between" mb={3} align="center">
              <HStack>
                <Badge colorScheme="blue" fontSize="0.8em">
                  {new Date(message.timestamp * 1000).toLocaleString()}
                </Badge>
              </HStack>
              <HStack spacing={3}>
                <Badge colorScheme="orange" variant="outline">
                  Input: {(message.input_chars || 0).toLocaleString()} chars
                </Badge>
                <Badge colorScheme="green" variant="outline">
                  Output: {(message.output_chars || 0).toLocaleString()} chars
                </Badge>
              </HStack>
            </Flex>
            
            <Box bg="blackAlpha.50" p={3} borderRadius="md" mb={3}>
              <Text fontWeight="bold" fontSize="sm" mb={1} color="gray.500">
                User Query:
              </Text>
              <Text fontSize="md" fontStyle="italic">
                {extractUserMessage(message.input)}
              </Text>
            </Box>
            
            <Box bg="blue.50" p={3} borderRadius="md" _dark={{ bg: "blue.900" }}>
              <Text fontWeight="bold" fontSize="sm" mb={1} color="gray.500">
                AI Response:
              </Text>
              <Text fontSize="md">
                {message.output || "No response recorded"}
              </Text>
            </Box>
          </Box>
        ))}
      </VStack>
    );
  };

  return (
    <Container maxW="container.xl" py={10}>
      <VStack spacing={10} align="stretch">
        <Box 
          p={10} 
          bg={bgColor} 
          borderRadius="2xl" 
          boxShadow="xl"
          position="relative"
          overflow="hidden"
        >
          <Box 
            position="absolute" 
            top={0} 
            left={0} 
            right={0} 
            height="8px" 
            bgGradient="linear(to-r, teal.400, blue.500, purple.600)"
          />
          
          <VStack spacing={6} align="center" textAlign="center">
            <Flex align="center" justify="space-between" w="full">
              <Icon as={FaChartLine} w={12} h={12} color={accentColor} />
              
              <Button 
                leftIcon={<FaRedo />}
                onClick={fetchUserStatistics}
                isLoading={isLoading}
                colorScheme="teal"
                variant="outline"
                size="sm"
              >
                Refresh
              </Button>
            </Flex>
            
            <Heading 
              size="2xl" 
              bgClip="text" 
              bgGradient="linear(to-r, teal.400, blue.500, purple.600)" 
              fontWeight="extrabold"
              letterSpacing="tight"
            >
              Widget Usage Analytics
            </Heading>
            
            {isLoading && (
              <Spinner size="xl" color={accentColor} thickness="4px" />
            )}
            
            {error && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                <AlertTitle>Error loading data!</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            {!isLoading && !error && usageData && renderStatistics()}
            
            {!isLoading && !error && !usageData && (
              <Text
                fontSize="xl"
                color={textColor}
                maxW="600px"
              >
                No usage data available yet. Start using your widget to generate analytics!
              </Text>
            )}
            
            {!isLoading && !error && usageData && renderRecentMessages()}
          </VStack>
        </Box>
      </VStack>
      
      <Divider my={10} />
      
      <Text
        textAlign="center"
        color="gray.500"
      >
        Usage data is updated in real time. Last updated: {usageData?.lastUpdated || 'Never'}
      </Text>
    </Container>
  );
}